import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useProgress } from "@/context/ProgressContext";
import { useToast } from "@/lib/toast";
import { BRAND, MODULES, QUICK_LINKS, TOTAL_LESSONS } from "@/data/modules";
import { ProgressBar, SectionTitle } from "@/components/ui";

export default function Home() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { countDone } = useProgress();

  const firstName = user?.name?.split(" ")[0] || "there";
  const doneTotal = MODULES.reduce((n, m) => n + countDone(m.lessons.map((l) => l.id)), 0);
  const overallPercent = TOTAL_LESSONS === 0 ? 0 : Math.round((doneTotal / TOTAL_LESSONS) * 100);
  const nextModule = MODULES.find((m) => countDone(m.lessons.map((l) => l.id)) < m.lessons.length) ?? MODULES[0];

  const editCard = (testid, label, hint) => (
    <button data-testid={testid} className="pressable" onClick={() => toast.show("You'll be able to edit this later.")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, background: colors.surfaceSecondary, borderRadius: 18, border: `1px solid ${colors.border}`, padding: 14, cursor: "pointer", textAlign: "left" }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: colors.surfaceTertiary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name="image" size={18} color={colors.muted} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: colors.onSurface, fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>{label}</div>
        <div style={{ color: colors.muted, fontSize: 12, marginTop: 2, fontFamily: fonts.text }}>{hint}</div>
      </div>
      <Icon name="edit-2" size={16} color={colors.muted} />
    </button>
  );

  return (
    <div className="ritzy-fade">
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${colors.divider}` }}>
        <div data-testid="home-greeting" style={{ color: colors.onSurface, fontSize: 28, fontFamily: fonts.display, fontWeight: 700 }}>Hey, {firstName} 👋</div>
        <div style={{ color: colors.muted, fontSize: 13, marginTop: 4, fontFamily: fonts.text }}>{BRAND.name} · {BRAND.tagline}</div>
      </div>

      <div style={{ padding: "20px 24px" }}>
        <div data-testid="training-progress-card" style={{ background: colors.brand, borderRadius: 24, padding: 20 }}>
          <div style={{ color: colors.onBrand, fontSize: 20, fontFamily: fonts.display, fontWeight: 700 }}>You're {overallPercent}% through your training</div>
          <div style={{ marginTop: 14 }}><ProgressBar percent={overallPercent} /></div>
          <div style={{ marginTop: 18, background: colors.surface, borderRadius: 18, padding: 16 }}>
            <div style={{ color: colors.muted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: fonts.text }}>Continue where you left off</div>
            <div style={{ color: colors.onSurface, fontSize: 16, fontWeight: 700, marginTop: 6, fontFamily: fonts.text }}>{nextModule.title}</div>
            <div style={{ color: colors.muted, fontSize: 13, marginTop: 2, fontFamily: fonts.text }}>{nextModule.subtitle}</div>
            <button data-testid="continue-training-button" className="pressable" onClick={() => navigate(`/module/${nextModule.id}`)} style={{ width: "100%", background: colors.brandPrimary, color: colors.onBrandPrimary, border: "none", borderRadius: 14, padding: "12px", marginTop: 14, cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>Pick it back up</button>
          </div>
        </div>

        <div style={{ marginTop: 24, marginBottom: 12 }}><SectionTitle>This month at {BRAND.name}</SectionTitle></div>
        {editCard("this-month-card", "Add this month's focus", "Highlight the current product, campaign or launch")}

        <div style={{ marginTop: 24, marginBottom: 12 }}><SectionTitle>Quick links</SectionTitle></div>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
          {QUICK_LINKS.map((q) => (
            <button key={q.id} data-testid={`quick-link-${q.id}`} className="pressable" onClick={() => (q.href ? navigate(q.href) : toast.show("Coming soon."))} style={{ width: "48%", display: "flex", alignItems: "center", gap: 10, background: colors.surfaceSecondary, borderRadius: 16, border: `1px solid ${colors.border}`, padding: "14px 12px", marginBottom: 12, cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={q.icon} size={18} color={colors.brand} />
              </div>
              <span style={{ flex: 1, color: colors.onSurface, fontSize: 13, fontWeight: 600, fontFamily: fonts.text }}>{q.label}</span>
            </button>
          ))}
        </div>

        <div style={{ marginTop: 24, marginBottom: 12 }}><SectionTitle>Latest update</SectionTitle></div>
        {editCard("latest-update-card", "Add latest update", "Share your newest note with the team")}
      </div>
    </div>
  );
}
